'use client'

import {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
  type ReactNode,
  type FC,
} from 'react'
import { useRouter, usePathname } from 'next/navigation'
import Image from 'next/image'
import AppIcon from '@/components/content/app/AppIcon'
import {
  createAppChipElement,
  createCollectionChipElement,
  createCategoryChipElement,
} from '@/lib/search-chip'
import { toSlug, removeMentions, slugMap, iconMap, getLabel } from '@/lib/utils'
import { createChat } from '@/lib/actions/chat'
import { prependSearchHistoryEntry } from '@/lib/search-history'
import { Icon } from '@/icons/Icon'
import { searchAlgolia, type AlgoliaSearchResult } from '@/lib/algolia-client'
import { queryPayload } from '@/lib/payloadClient'
import {
  parseVsQuery,
  findAppsByNames,
  validateComparisonApps,
  generateComparisonSlug,
} from '@/lib/search-utils'
import { getActiveInput, setActiveInput, type ActiveInput } from '@/lib/activeInput'
import { createLogger } from '@/lib/logger'
import { measureSync, markStart, markEnd } from '@/lib/perf'
import type { CachedSearchResult } from '@/types/chat'
import type { ResultItemProps, ResultType, SearchEntry, ResultItemApp } from '@/types/app'
import type {
  SearchShortcutIndex,
  SearchIndexApp,
  SearchIndexCategory,
  SearchIndexStack,
} from '@/lib/search-data'
import { ANALYTICS_ROUTES, isAnalyticsPath } from '@/lib/analytics-routes.mjs'

const logger = createLogger('ChatContext')

export type ContextType = 'search' | 'chat'

// Collection shortcut results (static - these don't change)
export const shortcuts: ResultItemProps[] = [
  { label: '/review', type: 'review', icon: <Icon name="review" />, shortcut: true },
  { label: '/deal', type: 'deal', icon: <Icon name="deal" />, shortcut: true },
  { label: '/best', type: 'best', icon: <Icon name="best" />, shortcut: true },
  { label: '/compare', type: 'comparison', icon: <Icon name="comparison" />, shortcut: true },
  { label: '/alternative', type: 'alternative', icon: <Icon name="alternative" />, shortcut: true },
  { label: '/stack', type: 'stack', icon: <Icon name="stack" />, shortcut: true },
]

// Recommended questions
const defaultQuestions: ResultItemProps[] = [
  {
    label: 'Best project management software for small businesses',
    type: 'chat',
    icon: <Icon name="chat" size={12} />,
  },
  {
    label: 'What software stack should I use for my startup?',
    type: 'chat',
    icon: <Icon name="chat" size={12} />,
  },
  {
    label: 'What CRM should I use for my small business?',
    type: 'chat',
    icon: <Icon name="chat" size={12} />,
  },
  {
    label: 'What project management software is best for small teams?',
    type: 'chat',
    icon: <Icon name="chat" size={12} />,
  },
]

const categoryCollections = ['best', 'stack']

// Detects "X vs Y" queries and returns a comparison result item if valid
function detectVsComparison(
  query: string,
  comparisonApps: SearchIndexApp[] | undefined,
): ResultItemProps | null {
  const vsApps = parseVsQuery(query)
  if (!vsApps || !comparisonApps) return null

  const foundApps = findAppsByNames(vsApps, comparisonApps)
  if (!foundApps || foundApps.length < 2 || !validateComparisonApps(foundApps)) return null

  const comparisonSlug = generateComparisonSlug(foundApps)
  return {
    type: 'comparison',
    label: `Compare ${foundApps.map((a) => a.name).join(' vs ')}`,
    slug: comparisonSlug,
    apps: foundApps.map((a) => a.name),
    icon: <Icon name="comparison" size={12} />,
  }
}

type AppCardProps = ResultItemApp

type ChatContextType = {
  isTraffic: boolean
  showSearch: boolean
  setShowSearch: Dispatch<SetStateAction<boolean>>
  showSpotlight: boolean
  setShowSpotlight: Dispatch<SetStateAction<boolean>>
  isMenuVisible: boolean
  setIsMenuVisible: Dispatch<SetStateAction<boolean>>
  query: string
  setQuery: Dispatch<SetStateAction<string>>
  context: 'search' | 'chat'
  setContext: Dispatch<SetStateAction<'search' | 'chat'>>
  results: ResultItemProps[]
  setResults: Dispatch<SetStateAction<ResultItemProps[]>>
  shortcuts: ResultItemProps[]
  collection: string | null
  setCollection: Dispatch<SetStateAction<ResultType | null>>
  changeCollection: (collection: ResultType) => void
  apps: AppCardProps[]
  setApps: Dispatch<SetStateAction<AppCardProps[]>>
  command: HTMLElement | null
  setCommand: Dispatch<SetStateAction<HTMLElement | null>>
  caret: { x: number; y: number } | null
  setCaret: Dispatch<SetStateAction<{ x: number; y: number } | null>>
  selectedIndex: number
  setSelectedIndex: Dispatch<SetStateAction<number>>
  showSuggestion: string | null
  setShowSuggestion: Dispatch<SetStateAction<string | null>>
  searching: boolean
  setSearching: Dispatch<SetStateAction<boolean>>
  openSearch: (source?: ActiveInput) => void
  closeSearch: (shouldClear?: boolean) => void
  handleInput: (event: FormEvent<HTMLDivElement>) => void
  handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  handleKeyTrigger: (event: globalThis.KeyboardEvent) => void
  addApp: (app: AppCardProps) => void
  insertCommand: (item: ResultItemProps) => void
  handleResultClick: (item: ResultItemProps) => void
  handleShortcutClick: (collection: ResultType) => void
  handleContextSwitch: (context: ContextType) => void
  handleSubmit: (options?: { forceChat?: boolean; queryOverride?: string }) => void
  insertPrompt: (prompt: string) => void
  fillChat: (prompt: string) => void
  selectedComparisonApps: ResultItemProps[]
  inputRef: RefObject<HTMLDivElement | null>
  commandRef: RefObject<MutationObserver | null>
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

export const useChatContext = () => {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext must be used within a ChatContextProvider')
  }
  return context
}

type ChatContextProviderProps = {
  children: ReactNode
  searchIndex?: SearchShortcutIndex
}

function appToResultItem(app: SearchIndexApp): ResultItemProps {
  return {
    type: 'app',
    label: app.name,
    slug: app.slug,
    app,
    categoryIds: app.categoryIds,
    primaryCategoryIds: app.primaryCategoryIds,
    icon: (
      <AppIcon
        name={app.name}
        size={20}
        icon={app.icon}
        glyph={app.glyph}
        background={app.brandBackgroundColor ?? '#ffffff'}
      />
    ),
  }
}

function categoryToResultItem(category: SearchIndexCategory): ResultItemProps {
  return {
    type: 'best',
    label: category.name,
    slug: category.slug,
    icon: <Icon name="best" />,
  }
}

function stackToResultItem(stack: SearchIndexStack): ResultItemProps {
  const companyApp = stack.companyStackApp ?? null

  return {
    type: 'stack',
    label: stack.name,
    slug: stack.slug,
    icon: companyApp ? (
      <AppIcon
        name={stack.name}
        size={22}
        icon={companyApp.icon}
        glyph={companyApp.glyph}
        background={companyApp.brandBackgroundColor ?? '#ffffff'}
      />
    ) : (
      <Icon name={stack.icon ?? 'stack'} />
    ),
  }
}

// Map Algolia collection slugs to ResultType
const algoliaCollectionToType: Record<string, ResultType> = {
  review: 'review',
  alternative: 'alternative',
  best: 'best',
  blog: 'blog',
  compare: 'comparison',
  comparison: 'comparison',
  course: 'course',
  page: 'page',
  stack: 'stack',
  video: 'search',
  deal_info: 'deal',
  deal: 'deal',
}

const collectionTypeLabels: Record<string, string> = {
  review: 'Review',
  alternative: 'Alternatives',
  best: 'Best',
  blog: 'Blog',
  comparison: 'Compare',
  compare: 'Compare',
  course: 'Course',
  page: 'Page',
  stack: 'Stack',
  video: 'Video',
  deal_info: 'Deal',
  deal: 'Deal',
}

// Helper to create a chat result item
function createChatResultItem(query: string): ResultItemProps {
  return {
    type: 'chat',
    label: query,
    slug: query,
    icon: <Icon name="chat" size={12} />,
  }
}

// Helper to convert Algolia result to ResultItemProps
function algoliaResultToItem(result: AlgoliaSearchResult): ResultItemProps {
  const type = algoliaCollectionToType[result.collectionSlug] || 'search'
  const typeLabel = collectionTypeLabels[result.collectionSlug] || ''
  const glyphData = result.appBranding?.glyphUrl
    ? {
        url: result.appBranding.glyphUrl,
        coveragePercent: result.appBranding.glyphCoveragePercent,
        xOffsetPercent: result.appBranding.glyphXOffsetPercent,
        yOffsetPercent: result.appBranding.glyphYOffsetPercent,
      }
    : undefined
  const hasBrandedLogo = Boolean(result.appBranding && (result.imageUrl || glyphData))

  // Use the slug field directly from Algolia record
  // All records now have a slug field set by the Algolia plugin builders
  const slug = result.slug || toSlug(result.name || '')

  // Build label with type suffix for disambiguation
  // e.g., "Notion" -> "Notion · Review" or "Notion · Alternatives"
  const label = result.name ? (typeLabel ? `${result.name} · ${typeLabel}` : result.name) : ''

  // Determine the icon to render
  let icon: ReactNode
  if (result.appBranding && hasBrandedLogo) {
    // Use AppIcon with branding for app-related results
    icon = (
      <AppIcon
        url={glyphData ? undefined : result.imageUrl}
        glyph={glyphData}
        name={result.name}
        background={result.appBranding?.backgroundColor ?? '#FFFFFF'}
        size={24}
      />
    )
  } else if (result.imageUrl) {
    // Fallback to plain image for results without branding
    icon = (
      <Image
        src={result.imageUrl}
        alt={result.name || 'Search result'}
        width={20}
        height={20}
        className="h-5 w-5 rounded object-cover"
      />
    )
  } else if (result.icon) {
    // Use custom icon from Algolia record (e.g., page collection)
    icon = <Icon name={result.icon} />
  } else {
    // No image or custom icon - use type icon
    icon = <Icon name={iconMap[type] || 'magnifier-small'} />
  }

  return {
    type,
    label,
    slug,
    icon,
  }
}

export const ChatContextProvider: FC<ChatContextProviderProps> = ({ children, searchIndex }) => {
  const router = useRouter()
  const pathname = usePathname()
  // Traffic pages get a restricted, app-name-only search experience (see the
  // isTraffic branches in handleInput/the search effect/handleResultClick)
  // instead of the full chat/command palette.
  const isTraffic = isAnalyticsPath(pathname)

  // Safari can throw "invariant expected app router to be mounted" in certain event chains.
  // Fall back to window.location for reliability across all browsers.
  const safeNavigate = useCallback(
    (path: string) => {
      try {
        router.push(path)
      } catch (error) {
        logger.error(
          'Failed to navigate to path. Falling back manually (safari workaround)',
          path,
          error,
        )
        window.location.href = path
      }
    },
    [router],
  )

  const inputRef = useRef<HTMLDivElement>(null)
  const commandRef = useRef<MutationObserver | null>(null)
  // Track if context was manually set by user (resets when query changes)
  const isManualContextRef = useRef<boolean>(false)

  const appsForIds = useCallback(
    (ids: number[] | undefined): SearchIndexApp[] => {
      if (!searchIndex || !ids) return []
      const out: SearchIndexApp[] = []
      for (const id of ids) {
        const app = searchIndex.appsById[id]
        if (app) out.push(app)
      }
      return out
    },
    [searchIndex],
  )

  const comparisonAppsList = useMemo(
    () =>
      measureSync('chat-ctx:comparisonAppsList', () => appsForIds(searchIndex?.comparisonAppIds)),
    [appsForIds, searchIndex?.comparisonAppIds],
  )

  const appResultsFromData = useMemo(
    () =>
      measureSync('chat-ctx:appResultsFromData', () => {
        if (!searchIndex) return []
        const seen = new Set<number>()
        const uniqueApps: SearchIndexApp[] = []
        for (const id of [...searchIndex.reviewAppIds, ...searchIndex.alternativeAppIds]) {
          if (seen.has(id)) continue
          const app = searchIndex.appsById[id]
          if (!app) continue
          seen.add(id)
          uniqueApps.push(app)
        }
        return uniqueApps.map(appToResultItem)
      }),
    [searchIndex],
  )

  const reviewAppResults = useMemo(
    () =>
      measureSync('chat-ctx:reviewAppResults', () =>
        appsForIds(searchIndex?.reviewAppIds).map(appToResultItem),
      ),
    [appsForIds, searchIndex?.reviewAppIds],
  )

  const dealAppResults = useMemo(
    () =>
      measureSync('chat-ctx:dealAppResults', () =>
        appsForIds(searchIndex?.dealAppIds).map(appToResultItem),
      ),
    [appsForIds, searchIndex?.dealAppIds],
  )

  const alternativeAppResults = useMemo(
    () =>
      measureSync('chat-ctx:alternativeAppResults', () =>
        appsForIds(searchIndex?.alternativeAppIds).map(appToResultItem),
      ),
    [appsForIds, searchIndex?.alternativeAppIds],
  )

  const categoryResults = useMemo(
    () =>
      measureSync('chat-ctx:categoryResults', () => {
        if (!searchIndex) return []
        const results: ResultItemProps[] = []
        for (const id of searchIndex.bestCategoryIds) {
          const category = searchIndex.categoriesById[id]
          if (category) results.push(categoryToResultItem(category))
        }
        return results
      }),
    [searchIndex],
  )

  const stackResults = useMemo(
    () =>
      measureSync('chat-ctx:stackResults', () => {
        if (!searchIndex) return []
        const results: ResultItemProps[] = []
        for (const id of searchIndex.stackIds) {
          const stack = searchIndex.stacksById[id]
          if (stack) results.push(stackToResultItem(stack))
        }
        return results
      }),
    [searchIndex],
  )

  const comparisonAppResults = useMemo(
    () =>
      measureSync('chat-ctx:comparisonAppResults', () => comparisonAppsList.map(appToResultItem)),
    [comparisonAppsList],
  )

  // Default results to show when search opens (general search suggestions)
  const defaultResults = useMemo(
    () =>
      measureSync('chat-ctx:defaultResults', () => {
        const results: ResultItemProps[] = []
        // Add some review apps as suggestions
        if (reviewAppResults.length > 0) {
          results.push(
            ...reviewAppResults.slice(0, 3).map((r) => ({ ...r, type: 'review' as ResultType })),
          )
        }
        // Add some deal apps
        if (dealAppResults.length > 0) {
          results.push(
            ...dealAppResults.slice(0, 2).map((r) => ({ ...r, type: 'deal' as ResultType })),
          )
        }
        // Add some stacks
        if (stackResults.length > 0) {
          results.push(...stackResults.slice(0, 2))
        }
        return results
      }),
    [reviewAppResults, dealAppResults, stackResults],
  )

  const [showSearch, setShowSearch] = useState(false)
  const [showSpotlight, setShowSpotlight] = useState(false)
  const [isMenuVisible, setIsMenuVisible] = useState(false)
  const [query, setQuery] = useState('')
  const [context, setContext] = useState<'search' | 'chat'>('search')
  const [results, setResults] = useState<ResultItemProps[]>([])
  const [collection, setCollection] = useState<ResultType | null>(null)
  const [apps, setApps] = useState<AppCardProps[]>([])
  const [command, setCommand] = useState<HTMLElement | null>(null)
  const [caret, setCaret] = useState<{ x: number; y: number } | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [showSuggestion, setShowSuggestion] = useState<string | null>(null)
  const [searching, setSearching] = useState<boolean>(false)
  // Track selected apps in comparison mode (stores ResultItemProps with categoryIds)
  const [selectedComparisonApps, setSelectedComparisonApps] = useState<ResultItemProps[]>([])
  // Store raw Algolia search results for passing to chat creation
  const [algoliaSearchResults, setAlgoliaSearchResults] = useState<AlgoliaSearchResult[]>([])

  // Reset search state on route changes
  useEffect(() => {
    if (showSearch) {
      clearSearch()
    }
    // Reset searching state after navigation completes
    // Small delay ensures the new page has loaded before removing the loading indicator
    const timer = setTimeout(() => {
      setSearching(false)
    }, 100)
    return () => clearTimeout(timer)
  }, [pathname])

  const addApp = (app: AppCardProps) => {
    const isSelected = apps.some((a) => a.id === app.id)
    if (isSelected) {
      setApps(apps.filter((a) => a.id !== app.id))
    } else {
      if (apps.length < 4) {
        setApps([...apps, app])
      }
    }
  }

  const handleSubmit = async (options?: { forceChat?: boolean; queryOverride?: string }) => {
    const effectiveQuery = options?.queryOverride ?? query
    if (effectiveQuery === '' || searching) return
    setSearching(true)

    // Use forceChat option to bypass async state update timing issues
    if (context === 'chat' || options?.forceChat) {
      // Use the already-fetched Algolia results from the debounced search effect
      // These results are based on user's typing which works better for Algolia matching
      let cachedSearchResults: CachedSearchResult[] = []

      // First try to use the stored results from the debounced search
      if (algoliaSearchResults.length > 0) {
        cachedSearchResults = algoliaSearchResults.map((hit) => ({
          objectID: hit.objectID,
          collectionSlug: hit.collectionSlug,
          collectionDocId: hit.collectionDocId,
          slug: hit.slug,
          name: hit.name,
          imageUrl: hit.imageUrl,
          // Pass related content from top-level fields (not nested in metadata)
          relatedApps: hit.relatedApps,
          relatedBestPages: hit.relatedBestPages,
          relatedComparisons: hit.relatedComparisons,
          relatedBlogs: hit.relatedBlogs,
          relatedDeal: hit.relatedDeal,
          relatedAlternativePage: hit.relatedAlternativePage,
          relatedAlternativeApps: hit.relatedAlternativeApps,
          relatedApp: hit.relatedApp,
        }))
      } else {
        // Fallback: extract keywords and search with those
        // Remove common filler words but KEEP valuable search terms like 'best'
        // Also remove generic terms like 'app', 'software' that don't help narrow results
        const keywords = effectiveQuery
          .toLowerCase()
          .replace(/[?!.,]/g, '')
          .split(' ')
          .filter(
            (word) =>
              ![
                // Filler words
                'what',
                'is',
                'the',
                'a',
                'an',
                'for',
                'to',
                'if',
                'i',
                'need',
                'pick',
                '1',
                // Generic terms that don't help with search
                'app',
                'apps',
                'software',
                'tool',
                'tools',
                'program',
                'programs',
                'application',
                'applications',
              ].includes(word),
          )
          .join(' ')

        if (keywords.trim()) {
          try {
            const algoliaResults = await searchAlgolia(keywords, { hitsPerPage: 8 })
            if (algoliaResults && algoliaResults.hits.length > 0) {
              // Filter out videos and dedupe deal_info records (same app can have multiple deals)
              const seen = new Set<string>()
              const filteredHits = algoliaResults.hits.filter((hit) => {
                if (hit.collectionSlug === 'video') return false
                // Dedupe deal_info by name (same app can have multiple deals)
                // For other collections, use objectID to preserve distinct documents with same name
                const key =
                  hit.collectionSlug === 'deal_info'
                    ? `${hit.collectionSlug}_${hit.name}`
                    : hit.objectID
                if (seen.has(key)) return false
                seen.add(key)
                return true
              })
              cachedSearchResults = filteredHits.map((hit) => ({
                objectID: hit.objectID,
                collectionSlug: hit.collectionSlug,
                collectionDocId: hit.collectionDocId,
                slug: hit.slug,
                name: hit.name,
                imageUrl: hit.imageUrl,
                // Pass related content from top-level fields
                relatedApps: hit.relatedApps,
                relatedBestPages: hit.relatedBestPages,
                relatedComparisons: hit.relatedComparisons,
                relatedBlogs: hit.relatedBlogs,
                relatedDeal: hit.relatedDeal,
                relatedAlternativePage: hit.relatedAlternativePage,
                relatedAlternativeApps: hit.relatedAlternativeApps,
                relatedApp: hit.relatedApp,
              }))
            }
          } catch (error) {
            logger.error('Algolia search error:', error)
          }
        }
      }

      // Generate UUID client-side so we can navigate immediately. Dispatch
      // the server action BEFORE navigating to ensure the fetch is in-flight
      // before the component could unmount. fetchChat retries until the
      // record exists.
      const uuid = crypto.randomUUID()

      createChat(effectiveQuery, cachedSearchResults, uuid).catch((error) => {
        logger.error('Failed to create chat:', error)
      })

      const entry: SearchEntry = {
        id: uuid,
        query: effectiveQuery,
        timestamp: new Date(),
      }

      prependSearchHistoryEntry(entry)

      sessionStorage.setItem('pending-search-query', effectiveQuery)
      safeNavigate(`/search/${uuid}`)
      clearSearch()
    } else {
      if (!collection || !effectiveQuery) {
        logger.error('Cannot submit: collection or query is missing', {
          collection,
          effectiveQuery,
        })
        setSearching(false)
        return
      }

      const slug = toSlug(effectiveQuery).replace(/^-[^-]+-?/, '')
      const link = `/${slugMap[collection as ResultType]}/${slug}`
      safeNavigate(link)
      clearSearch()
    }
  }

  // Traffic pages: app-name-only search, no Algolia/chat/command palette
  useEffect(() => {
    if (!isTraffic) return

    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setResults([])
      setSelectedIndex(0)
      return
    }

    let isCancelled = false
    const debounceTimer = setTimeout(async () => {
      try {
        const result = await queryPayload({
          collection: 'app',
          where: { name: { contains: trimmed } },
          limit: 6,
          depth: 0,
          select: { name: true, slug: true },
        })
        if (isCancelled) return
        const appResults: ResultItemProps[] = result.docs
          .filter((doc) => Boolean(doc.name && doc.slug))
          .map((doc) => ({
            type: 'app',
            label: doc.name as string,
            slug: doc.slug as string,
            icon: <Icon name="magnifier-small" size={12} />,
          }))
        setResults(appResults)
        setSelectedIndex(0)
      } catch (error) {
        if (isCancelled) return
        logger.error('Traffic app search error:', error)
        setResults([])
      }
    }, 200)

    return () => {
      clearTimeout(debounceTimer)
      isCancelled = true
    }
  }, [isTraffic, query])

  // General search: Algolia + "vs" detection
  // This effect handles searching when the user types in general search mode (no command active)
  useEffect(() => {
    // Traffic pages use their own app-only search effect above
    if (isTraffic) return

    // Only run for general search mode (no command)
    if (command) return

    // Don't search empty queries
    if (!query.trim()) {
      // If we're in chat mode but it wasn't manually set, switch back to search
      if (context === 'chat' && !isManualContextRef.current) {
        setContext('search')
        setResults(shortcuts)
      } else {
        setResults(context === 'chat' ? defaultQuestions : shortcuts)
      }
      return
    }

    // Check for "vs" query pattern first (skip when user manually selected chat)
    if (!isManualContextRef.current) {
      const comparisonResult = detectVsComparison(query, comparisonAppsList)
      if (comparisonResult) {
        setResults([comparisonResult, ...shortcuts])
        setSelectedIndex(0)
        return
      }
    }

    // Track if this effect instance has been superseded by a newer one
    let isCancelled = false

    // Debounce Algolia search
    const debounceTimer = setTimeout(async () => {
      try {
        const algoliaResults = await searchAlgolia(query, { hitsPerPage: 8 })

        // Don't update state if this effect has been cleaned up (newer query started)
        if (isCancelled) return

        if (algoliaResults && algoliaResults.hits.length > 0) {
          setAlgoliaSearchResults(algoliaResults.hits) // Store raw results for RAG context

          // Only auto-switch to search if currently in search context OR if chat wasn't manually set
          if (context === 'search' || !isManualContextRef.current) {
            setContext('search')
            // Show search results only in search mode
            const seen = new Set<string>()
            const filteredHits = algoliaResults.hits.filter((hit) => {
              if (hit.collectionSlug === 'video') return false
              // Dedupe deal_info by name (same app can have multiple deals)
              // For other collections, use objectID to preserve distinct documents with same name
              const key =
                hit.collectionSlug === 'deal_info'
                  ? `${hit.collectionSlug}_${hit.name}`
                  : hit.objectID
              if (seen.has(key)) return false
              seen.add(key)
              return true
            })

            // If all results were filtered out (e.g., only videos), treat as no results
            if (filteredHits.length === 0) {
              setContext('chat')
              setResults([createChatResultItem(query)])
              setAlgoliaSearchResults([])
              setSelectedIndex(0)
            } else {
              const convertedResults = filteredHits.map(algoliaResultToItem)
              setResults([...convertedResults, ...shortcuts])
              setAlgoliaSearchResults(filteredHits) // Store raw results for RAG context
              setSelectedIndex(0)
            }
          } else {
            // In chat mode (manually set) - show chat result
            setResults([createChatResultItem(query)])
            setSelectedIndex(0)
          }
        } else {
          // No results from Algolia - automatically switch to chat context only if in search mode
          if (context === 'search') {
            setContext('chat')
          }
          // Show a "chat" option to search with AI
          setResults([createChatResultItem(query)])
          setAlgoliaSearchResults([]) // Clear previous results
          setSelectedIndex(0)
        }
      } catch (error) {
        // Don't log errors for cancelled requests
        if (isCancelled) return
        logger.error('Algolia search error:', error)
      }
    }, 100) // 100ms debounce

    return () => {
      clearTimeout(debounceTimer)
      isCancelled = true
    }
    // `context` is intentionally omitted: re-running this effect on context
    // changes would re-fire Algolia mid-debounce. Staleness is bounded by
    // isManualContextRef.current, which is read fresh from the ref.
    // `isTraffic` IS included so switching to a traffic page cancels any
    // in-flight general search (its cleanup runs) before stale Algolia results
    // can overwrite the traffic app-search results.
  }, [query, command, comparisonAppsList, isTraffic])

  const openSearch: (source?: ActiveInput) => void = useCallback(
    (source?: ActiveInput) => {
      markStart('chat-ctx:openSearch')
      const wasAlreadyOpen = showSearch

      // Reset manual context flag when opening search
      if (!wasAlreadyOpen) {
        isManualContextRef.current = false
      }

      // Set active input based on source
      // If source is provided, use it; otherwise default to current active input
      if (source) {
        setActiveInput(source)
      }

      if (!showSearch) {
        setShowSearch(true)
      }

      const input = inputRef?.current
      input?.focus()

      // Only move cursor to the end when initially opening search
      if (!wasAlreadyOpen) {
        requestAnimationFrame(() => {
          const el = inputRef?.current
          if (!el) {
            markEnd('chat-ctx:openSearch')
            return
          }

          const range = document.createRange()
          const sel = window.getSelection()

          if (el.childNodes.length > 0) {
            const lastNode = el.childNodes[el.childNodes.length - 1]
            range.setStartAfter(lastNode)
            range.collapse(true)
          } else {
            range.selectNodeContents(el)
            range.collapse(false)
          }

          sel?.removeAllRanges()
          sel?.addRange(range)
          markEnd('chat-ctx:openSearch')
        })
      } else {
        markEnd('chat-ctx:openSearch')
      }
    },
    [showSearch, setShowSearch],
  )

  useEffect(() => {
    const isChat = getActiveInput() === 'chat'
    const hasCommand = Boolean(command)
    const canShowBase = showSearch && !isMenuVisible

    let shouldShowSpotlight: boolean

    if (isChat) {
      // In chat: hide if command exists, otherwise follow base gating
      shouldShowSpotlight = canShowBase && !hasCommand
    } else {
      // Outside chat: show even with command, still follow base gating
      shouldShowSpotlight = canShowBase
    }

    setShowSpotlight(shouldShowSpotlight)
  }, [showSearch, command, isMenuVisible])

  // Watch for app tag removals and sync with apps state
  useEffect(() => {
    if (!inputRef.current) return

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement
            const appId = element.dataset?.appId
            if (appId) {
              setApps((prevApps) => prevApps.filter((app) => String(app.id) !== appId))
              setSelectedComparisonApps((prev) =>
                prev.filter((item) => String(item.app?.id) !== appId),
              )
            }
          }
        })
      })
    })

    observer.observe(inputRef.current, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [inputRef])

  const resetValues = useCallback(() => {
    setCommand(null)
    setSelectedIndex(0)
    setResults(isTraffic ? [] : shortcuts)
    setCollection(null)
    setQuery('')
    setSelectedComparisonApps([]) // Clear comparison selections
    setAlgoliaSearchResults([]) // Clear Algolia results
  }, [isTraffic])

  const closeSearch = useCallback(
    (shouldClear?: boolean) => {
      inputRef?.current?.blur()
      setShowSearch(false)
      removeCommand()
      // Reset manual context flag when closing search
      isManualContextRef.current = false

      // Optionally clear search state
      if (shouldClear) {
        setApps([])
        resetValues()
        setContext('search')
        if (inputRef.current) {
          inputRef.current.textContent = ''
        }
      }
    },
    [resetValues],
  )

  const handleKeyTrigger: (event: globalThis.KeyboardEvent) => void = (event) => {
    const activeElement = document.activeElement
    const isInputFocused =
      activeElement instanceof HTMLDivElement ||
      (activeElement instanceof HTMLElement && activeElement.isContentEditable)

    if (
      !showSearch &&
      !isMenuVisible &&
      !isInputFocused &&
      ((event.key === 'k' && event.metaKey) ||
        ((event.key === '/' || event.code === 'Slash') && !event.shiftKey))
    ) {
      event.preventDefault()
      openSearch()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closeSearch(true)
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
    }
  }

  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const text = (el.innerText || '').replace(/\s+/g, ' ').trim()

    // Detect newly typed trigger character (disabled on traffic: app-name-only search)
    const lastChar = text.slice(-1)
    if (!isTraffic && (lastChar === '/' || lastChar === '@' || lastChar === '#') && !command) {
      // If slash, clear everything. If @, keep existing content
      if (lastChar === '/') {
        el.textContent = ''
        setQuery('/')

        // Move cursor to end after clearing
        const range = document.createRange()
        const sel = window.getSelection()
        range.selectNodeContents(el)
        range.collapse(false)
        sel?.removeAllRanges()
        sel?.addRange(range)
      } else {
        // For @, remove only the last typed character without destroying existing tags
        const sel = window.getSelection()
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0)
          // Delete the @ character that was just typed
          range.setStart(range.endContainer, range.endOffset - 1)
          range.deleteContents()
        }
      }

      addCommand(lastChar)
      return
    }

    // Update your query normally
    setQuery(text)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const selected = results[selectedIndex]

      if (selected) {
        if (selected.shortcut) {
          handleShortcutClick(selected.type)
        } else {
          handleResultClick(selected)
        }
      }
    } else if ((e.key === 'Backspace' || e.key === 'Delete') && command) {
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0)
        const selectedText = range.toString()

        if (selectedText.length > 0) {
          removeCommand()
          return
        }
      }

      const firstChild = command.firstElementChild as HTMLElement | null
      const onlyTriggerKey =
        firstChild?.textContent === '/' ||
        firstChild?.textContent === '@' ||
        firstChild?.textContent === '#'

      if (onlyTriggerKey) {
        e.preventDefault()
        removeCommand()
        return
      }
    }
  }

  const handleContextSwitch = (context: ContextType) => {
    setContext(context)
    // Only mark as manual if switching TO chat mode
    // Switching TO search mode should restore automatic behavior
    isManualContextRef.current = context === 'chat'
    setSelectedIndex(0)

    // When switching to chat mode, clear search results and show appropriate content
    if (!query.trim()) {
      if (context === 'chat') {
        setResults(defaultQuestions)
      } else {
        setResults(shortcuts)
      }
    } else {
      if (context === 'chat') {
        setResults([createChatResultItem(query)])
      } else {
        // Re-run vs-pattern detection when switching back to search
        // (the useEffect won't re-fire since context isn't in its deps)
        const comparisonResult = detectVsComparison(query, comparisonAppsList)
        if (comparisonResult) {
          setResults([comparisonResult, ...shortcuts])
          setSelectedIndex(0)
        } else {
          const convertedResults = algoliaSearchResults.map(algoliaResultToItem)
          setResults([...convertedResults, ...shortcuts])
        }
      }
    }

    openSearch()
    refocusCursor()
  }

  const handleShortcutClick = (collection: ResultType) => {
    changeCollection(collection)
    inputRef.current?.focus()
  }

  // Command Actions

  const addCommand = (key: string) => {
    const sel = window.getSelection()
    if (!sel || !inputRef.current) return

    const span = document.createElement('span')
    span.contentEditable = 'true'
    span.className =
      'bg-gray-100 leading-none dark:bg-white/10 h-fit inline-flex gap-px items-center rounded-md px-2'
    span.innerHTML = `
        <span class="text-gray-950 dark:text-white text-15">${key}</span>
        <span class="text-gray-400 dark:text-gray-600 text-16" contenteditable="false">type to filter</span>
      `

    const keySpan = span.firstElementChild as HTMLElement
    const placeholderSpan = span.lastElementChild as HTMLElement

    const range = sel.getRangeAt(0)
    range.deleteContents()
    range.insertNode(span)

    const newRange = document.createRange()
    newRange.setStartAfter(keySpan)
    newRange.setEndAfter(keySpan)
    sel.removeAllRanges()
    sel.addRange(newRange)

    setCommand(span)
    updateCaretPos(span)

    if (commandRef.current) commandRef.current.disconnect()

    commandRef.current = new MutationObserver(() => {
      const currentText = span.textContent || ''
      const expectedText = `${key} type to filter`
      if (currentText !== expectedText && placeholderSpan.parentNode) {
        placeholderSpan.remove()
        commandRef.current?.disconnect()
        commandRef.current = null
      }
    })

    commandRef.current.observe(span, { characterData: true, childList: true, subtree: true })
  }

  // Handle clicking on a result in the Spotlight dropdown (default results without active command)
  const handleResultClick = (item: ResultItemProps) => {
    if (searching) return

    if (isTraffic) {
      if (!item.slug) return
      setSearching(true)
      safeNavigate(`${ANALYTICS_ROUTES.report}/${encodeURIComponent(item.slug)}`)
      clearSearch()
      return
    }

    // If there's an active command, use insertCommand instead
    if (command && results.length > 0) {
      insertCommand(item)
      return
    }

    if (item.type === 'chat') {
      setContext('chat')
      // Prefer current query state (always up-to-date via setQuery) over
      // item.label which can lag behind during fast typing because the
      // Algolia debounce snapshots query at fire time. Fall back to
      // item.label for default questions where query is empty.
      handleSubmit({ forceChat: true, queryOverride: query || item.label })
      return
    } else {
      // For search-type results in chat context, trigger chat creation instead of direct navigation
      // This prevents race condition where direct navigation to /search/{query} happens before UUID is ready
      if (item.type === 'search' && context === 'chat') {
        handleSubmit()
        return
      }

      // Navigate directly based on result type
      setSearching(true)
      const slug = item.slug || item.label?.toLowerCase().replace(/\s+/g, '-')
      const route = slugMap[item.type]

      if (slug && route !== undefined) {
        // Handle empty route (pages are at root: /{slug})
        const path = route ? `/${route}/${slug}` : `/${slug}`
        safeNavigate(path)
        clearSearch()
      }
    }
  }

  const goToComparison = (item?: ResultItemProps) => {
    setSearching(true)

    const appsToCompare = item ? [...selectedComparisonApps, item] : selectedComparisonApps
    const appSlug = appsToCompare.map(
      (a) => a?.slug || a?.label?.toLowerCase().replace(/\s+/g, '-'),
    )

    safeNavigate(`/compare/${appSlug.join('-vs-')}`)
    clearSearch()
  }

  const insertCommand = (item: ResultItemProps) => {
    if (!command) return

    // Handle "Go to comparison" click
    if (item.type === 'comparison' && item.label === 'Go to comparison') {
      goToComparison()
      return
    }

    const sel = window.getSelection()
    const key = item.label?.charAt(0) || '/'
    const text = item.label?.slice(1) || ''

    const isCollection = key === '/'
    const isApp = item.type === 'app'
    const isCategory = categoryCollections.includes(item.type)

    let tag: HTMLElement

    if (isApp && item.app) {
      tag = createAppChipElement({
        name: item.label ?? '',
        icon: item.app.icon,
        glyph: item.app.glyph,
        backgroundColor: item.app.brandBackgroundColor ?? '#ffffff',
      })
      if (item.app.id) {
        tag.dataset.appId = String(item.app.id)
      }
    } else if (isCollection) {
      tag = createCollectionChipElement({ triggerKey: key, label: getLabel(text) })
    } else if (isCategory) {
      tag = createCategoryChipElement({ category: item.label || '' })
    } else {
      return
    }

    if (!command.isConnected) {
      if (inputRef.current) {
        inputRef.current.appendChild(tag)
      } else {
        return
      }
    } else {
      command.replaceWith(tag)
    }

    const space = document.createTextNode('\u00A0')
    tag.after(space)

    if (space.parentNode && sel) {
      const range = document.createRange()
      range.setStartAfter(space)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
    } else if (inputRef.current && sel) {
      const fallbackRange = document.createRange()
      const fallbackNode = inputRef.current
      fallbackRange.selectNodeContents(fallbackNode)
      fallbackRange.collapse(false)
      sel.removeAllRanges()
      sel.addRange(fallbackRange)
    }

    if (isCollection) {
      setCollection(item.type)
      setContext('search')
      setQuery(`${key}${text}`)

      // Don't reset values yet - we're adding another command
      setCommand(null)
      setSelectedIndex(0)

      // Add app command after collection
      addCommand(isCategory ? '#' : '@')
    } else if ((isApp || isCategory) && collection !== null) {
      if (collection === 'comparison') {
        // Track the selected app for comparison validation
        setSelectedComparisonApps((prev) => [...prev, item])

        // Only add "vs" and another command if we haven't reached the limit of 4 apps
        if (selectedComparisonApps.length < 3) {
          // For comparisons, add " vs " and another app command
          const vsText = document.createTextNode('vs ')
          const lastSpace = tag.nextSibling
          if (lastSpace) {
            lastSpace.after(vsText)

            // Update cursor position after " vs "
            const newRange = document.createRange()
            newRange.setStartAfter(vsText)
            newRange.collapse(true)
            sel?.removeAllRanges()
            sel?.addRange(newRange)
          }

          setQuery(`${query} vs `)
          setCommand(null)
          setSelectedIndex(0)

          // Add another app command
          addCommand('@')
        } else {
          goToComparison(item)
        }
      } else {
        // For other collections, navigate to the URL
        setSearching(true)
        const appSlug = item.slug || item.label?.toLowerCase().replace(/\s+/g, '-')
        safeNavigate(`/${slugMap[collection]}/${appSlug}`)
        clearSearch()
      }
    } else {
      const queryString = `${query === '' ? '' : `${query} `}${key}${text}`

      if (item.type === 'app' && item.app) {
        addApp(item.app)
        setQuery(removeMentions(queryString))
      } else {
        setQuery(queryString)
      }

      setCommand(null)
    }
  }

  const defineCollection = (collection: string) => {
    if (!inputRef.current) return

    // Focus the input first
    inputRef.current.focus()

    const sel = window.getSelection()
    if (!sel) return

    // Clear any existing content
    inputRef.current.textContent = ''

    // Create the collection tag
    const tag = createCollectionChipElement({ triggerKey: '/', label: getLabel(collection) })

    // Insert at the beginning
    inputRef.current.appendChild(tag)

    // Add space after tag
    const space = document.createTextNode(' ')
    tag.after(space)

    // Move cursor after the space
    const range = document.createRange()
    range.setStartAfter(space)
    range.collapse(true)
    sel.removeAllRanges()
    sel.addRange(range)

    setQuery(`/${collection}`)
    addCommand(categoryCollections.includes(collection) ? '#' : '@')
  }

  const removeCommand = () => {
    if (command) {
      command.remove()
      resetValues()
    }
  }

  // Clear the input and insert the prompt text
  const refocusCursor = () => {
    if (inputRef.current) {
      inputRef.current.focus()

      // Move cursor to the end
      const sel = window.getSelection()
      if (sel) {
        const range = document.createRange()
        range.selectNodeContents(inputRef.current)
        range.collapse(false)
        sel.removeAllRanges()
        sel.addRange(range)
      }
    }
  }

  const clearSearch = useCallback(() => {
    if (getActiveInput() === 'chat') return
    closeSearch(true)
  }, [closeSearch])

  const insertPrompt = (mention: string) => {
    /*
    let prompt: string
    switch (collection) {
      case 'review':
        prompt = `What's good about ${mention}`
        break
      case 'comparison':
        prompt = `Compare the following apps: ${mention}`
        break
      case 'best':
        prompt = `What's the best ${mention}`
        break
      case 'deal':
        prompt = `Show me the deals for ${mention}`
        break
      case 'alternative':
        prompt = `Show me some alternatives to ${mention}`
        break
      case 'stack':
        prompt = `What's a good stack for ${mention}`
        break
      default:
        prompt = `I want to know more about ${mention}`
    }
        */

    removeCommand()
    refocusCursor()
    setContext('chat')
    setQuery(mention)
  }

  const fillChat = useCallback(
    (mention: string) => {
      setApps([])
      resetValues()
      removeCommand()
      setContext('chat')
      setQuery(mention)
      if (inputRef.current) {
        inputRef.current.textContent = mention
      }
      refocusCursor()
    },
    [resetValues],
  )

  // Toolbar
  const changeCollection = (collection: ResultType) => {
    setCollection(collection)
    defineCollection(collection)
  }

  // Caret Positioning
  const updateCaretPos = (el: HTMLElement) => {
    if (!inputRef.current) return
    const triggerRect = el.getBoundingClientRect()
    const editorRect = inputRef.current.getBoundingClientRect()
    setCaret({
      x: triggerRect.left - editorRect.left + 44,
      y: triggerRect.bottom - editorRect.top + 16,
    })
  }

  useEffect(() => {
    if (!command) return
    const onInput = () => updateCaretPos(command)
    command.addEventListener('input', onInput)
    return () => command.removeEventListener('input', onInput)
  }, [command])

  useEffect(() => {
    if (!command) return
    markStart('chat-ctx:filter-results-effect')

    const fullText = command.textContent?.trim() || ''
    const triggerKey = fullText.charAt(0)
    const userText = fullText.replace('type to filter', '').trim().toLowerCase()

    // Determine which results to use based on trigger key and current collection context
    const isCollectionCommand = triggerKey === '/'
    const isAppMention = triggerKey === '@'
    const isCategoryMention = triggerKey === '#'

    let sourceResults: ResultItemProps[]

    if (isCollectionCommand) {
      // User is typing a collection shortcut like /review
      sourceResults = shortcuts
    } else if (isAppMention) {
      // User is selecting an app - use appropriate list based on collection
      switch (collection) {
        case 'review':
          sourceResults = reviewAppResults
          break
        case 'deal':
          sourceResults = dealAppResults
          break
        case 'alternative':
          sourceResults = alternativeAppResults
          break
        case 'comparison':
          // Max 4 apps in comparison - stop showing results once limit reached
          if (selectedComparisonApps.length >= 4) {
            sourceResults = []
            break
          }

          // For comparisons, use apps with category data
          // Filter out already-selected apps and enforce category overlap
          sourceResults = comparisonAppResults.filter((result) => {
            // Filter out already-selected apps
            if (selectedComparisonApps.some((selected) => selected.app?.id === result.app?.id)) {
              return false
            }

            // If this is the first app, show all available apps
            if (selectedComparisonApps.length === 0) {
              return true
            }

            // For subsequent apps, only show apps that share at least one category
            // with ALL already-selected apps (intersection, not union), AND at
            // least one of the apps in the resulting comparison must have that
            // shared category classified as primary. Secondary-only overlap is
            // not enough to justify a comparison page.
            const categoryArrays = selectedComparisonApps.map(
              (selected) => new Set(selected.categoryIds || []),
            )
            const candidateCategoryIds = new Set(result.categoryIds || [])
            const sharedCategories = [...categoryArrays, candidateCategoryIds].reduce(
              (intersection, currentSet) => {
                return new Set([...intersection].filter((catId) => currentSet.has(catId)))
              },
            )

            if (sharedCategories.size === 0) return false

            // Union of every app's primary category IDs (selected + candidate)
            const primaryCategoryUnion = new Set<number>()
            for (const selected of selectedComparisonApps) {
              for (const catId of selected.primaryCategoryIds || []) {
                primaryCategoryUnion.add(catId)
              }
            }
            for (const catId of result.primaryCategoryIds || []) {
              primaryCategoryUnion.add(catId)
            }

            // At least one shared category must be primary for at least one app
            return [...sharedCategories].some((catId) => primaryCategoryUnion.has(catId))
          })
          break
        default:
          sourceResults = appResultsFromData
      }
    } else if (isCategoryMention) {
      // User is selecting a category (for /best or /stack)
      if (collection === 'stack') {
        sourceResults = stackResults
      } else {
        sourceResults = categoryResults
      }
    } else {
      sourceResults = defaultResults
    }

    // Filter results if user has typed beyond the trigger key
    const filteredResults =
      userText === triggerKey
        ? sourceResults
        : sourceResults.filter((item) => {
            const filterQuery = isCollectionCommand ? userText : userText.slice(1)
            return item.label?.toLowerCase()?.includes(filterQuery)
          })

    // Inject "Go to comparison" item if we have 2+ selected apps in comparison mode
    let finalResults = filteredResults
    const isActiveComparison = collection === 'comparison' && selectedComparisonApps.length >= 2
    if (isActiveComparison) {
      const comparisonItem: ResultItemProps = {
        type: 'comparison',
        label: 'Go to comparison',
        icon: <Icon name="comparison" size={12} />,
      }
      finalResults = [comparisonItem, ...filteredResults]
    }

    // Filter out apps that are already in the apps state array
    const filteredFinalResults = finalResults.filter((result) => {
      if (!result.app?.id) return true
      return !apps.some((app) => app.id === result.app?.id)
    })

    if (!command?.textContent?.includes('type to filter') && isActiveComparison) {
      setSelectedIndex(1)
    } else {
      setSelectedIndex(0)
    }

    setResults(filteredFinalResults)
    markEnd('chat-ctx:filter-results-effect')
  }, [
    command,
    command?.textContent,
    collection,
    reviewAppResults,
    dealAppResults,
    alternativeAppResults,
    appResultsFromData,
    comparisonAppResults,
    selectedComparisonApps,
    categoryResults,
    stackResults,
    defaultResults,
    apps,
  ])

  useEffect(() => {
    return () => commandRef.current?.disconnect()
  }, [])

  return (
    <ChatContext.Provider
      value={{
        isTraffic,
        showSearch,
        setShowSearch,
        showSpotlight,
        setShowSpotlight,
        isMenuVisible,
        setIsMenuVisible,
        query,
        setQuery,
        context,
        setContext,
        results,
        setResults,
        shortcuts,
        collection,
        setCollection,
        changeCollection,
        apps,
        setApps,
        command,
        setCommand,
        caret,
        setCaret,
        selectedIndex,
        setSelectedIndex,
        showSuggestion,
        setShowSuggestion,
        searching,
        setSearching,
        openSearch,
        closeSearch,
        handleInput,
        handleKeyDown,
        handleKeyTrigger,
        addApp,
        handleSubmit,
        insertCommand,
        handleResultClick,
        handleShortcutClick,
        handleContextSwitch,
        insertPrompt,
        fillChat,
        selectedComparisonApps,
        inputRef,
        commandRef,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}
